import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { OverviewDetailsProps } from './interfaces/interface';
import TenantLists from '../TenantLists';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Button } from '../../ui/button';
import { RefreshCw } from 'lucide-react';

import OverviewResourceTypes from './OverviewResourceTypes';
import OverviewEventTimeline from './OverviewEventTimeline';
import TopResourceActivity from './TopResourceActivity';
import OverviewRecentActivity from './OverviewRecentActivity';
import RecentLoginDetails from './RecentLoginDetails';

import { getOverView, getRecentLoginData } from './Api/overviewapi';
import useUserStore from '../../../services/userStore';
import { myLocalStorage } from '../../StorageHelper';
import {
  mapEventTimeLine,
  mapResourceType,
  mapTopComputer,
  mapTopResources,
  mapTopUsers
} from './helper';
import { RecentLoginData } from '../interfaces/CommonInterfaces';
import { formatTimeAgo } from '../formatTimeAgo';
import { useCache } from '../../../utils/CacheContext';

export default function OverviewDetails({ onSelectorChange, fromdetails = false, computerName = "", computerId = "" }: OverviewDetailsProps) {
  const MAX_RECENT_ACTIVITY_ITEMS = 100;
  const [selectedTime, setSelectedTime] = useState<string>("lw");
  const [selectedTenant, setSelectedTenant] = useState(myLocalStorage.getItem("latestTenant")?.tenantName || "");

  const email = useUserStore((state) => state.user?.email);
  const tenantName = selectedTenant || myLocalStorage.getItem("latestTenant")?.tenantName || "";

  // Overview state
  const [eventTimeLine, setEvenTimeLine] = useState<any[]>([]);
  const [resourceType, setResourceType] = useState<any[]>([]);
  const [resourceName, setResourceName] = useState<any[]>([]);
  const [topUsers, setTopUsers] = useState<any[]>([]);
  const [topComputers, setTopComputers] = useState<any[]>([]);
  const [top10000Results, setTop10000Results] = useState<any[]>([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingLogins, setLoadingLogins] = useState(true);
  const { fetchCacheData, setCacheData, removeCacheData } = useCache();


  // Recent logins
  const [recentLogins, setRecentLogins] = useState<RecentLoginData[]>([]);

  const normalizeRecentActivity = useCallback((data: any[] = []) => {
    if (!Array.isArray(data)) return [];

    return data.slice(0, MAX_RECENT_ACTIVITY_ITEMS).map((item: any, index: number) => {
      const timestamp = item?.timestamp || item?._time || "";
      const description = item?.description || item?.message || "";
      const event = item?.event || item?.resourceType || "Unknown";

      return {
        id: item?.id || `${item?.computerId || index}-${index}`,
        event,
        from: item?.from || item?.tenantName || "Unknown",
        details: item?.details || description,
        time: item?.time || (timestamp ? formatTimeAgo(timestamp) : ""),
        description,
        computer: item?.computer || item?.computerName || "",
        user: item?.user || item?.computerUserEmail || "",
        path: item?.path || item?.resourceName || "",
      };
    });
  }, []);

  const mappedResourceType = useMemo(() => mapResourceType(resourceType), [resourceType]);
  const mappedEventTimeline = useMemo(() => mapEventTimeLine(eventTimeLine), [eventTimeLine]);
  const mappedTopUsers = useMemo(() => mapTopUsers(topUsers), [topUsers]);
  const mappedTopResources = useMemo(() => mapTopResources(resourceName), [resourceName]);
  const mappedTopComputers = useMemo(() => mapTopComputer(topComputers), [topComputers]);

  /**
   * Fetch overview data
   */
  const fetchOverViewData = async (bypassCache = false) => {
    setLoadingOverview(true);
    const key = "overviewKey";
    const fetchedData = bypassCache ? null : await fetchCacheData(key);

    try {

      if (fetchedData && !fromdetails) {
        let {
          eventTimeLine,
          resourceType,
          resourceName,
          computerUserEmail,
          computerName,
          lastest1000Result,
        } = fetchedData;
        setEvenTimeLine(eventTimeLine);
        setResourceType(resourceType);
        setResourceName(resourceName);
        setTopUsers(computerUserEmail);
        setTopComputers(computerName);
        const recentActivity = normalizeRecentActivity(lastest1000Result || []);
        setTop10000Results(recentActivity);
      } else {
        const response = await getOverView(
          email,
          tenantName,
          "ondevice-auditlog",
          selectedTime,
          computerName,
          computerId,
          key,
          fromdetails
        );

        if (response) {
          const {
            eventTimeLine,
            resourceType,
            resourceName,
            computerUserEmail,
            computerName,
            lastest1000Result
          } = response as any;
          const recentActivity = normalizeRecentActivity(lastest1000Result || []);
          if (!fromdetails) {
            setCacheData(
              {
                ...(response as any),
                lastest1000Result: recentActivity,
              },
              key
            );

          }
          setEvenTimeLine(eventTimeLine);
          setResourceType(resourceType);
          setResourceName(resourceName);
          setTopUsers(computerUserEmail);
          setTopComputers(computerName);
          setTop10000Results(recentActivity);
        } else {
          console.log("No data received");
        }
      }



    } catch (error) {
      console.error("Error fetching overview data:", error);
    }
    setLoadingOverview(false);
  };

  /**
   * Fetch recent login data
   */
  const fetchRecentLoginData = async () => {
    setLoadingLogins(true);
    try {
      const response = await getRecentLoginData(
        email,
        tenantName,
        "ondevice-auditlog",
        selectedTime,
        computerName,
        computerId,
        "recentlogin",
        fromdetails
      );

      if (response) {
        const recentLoginsArray = (response as any) || [];

        const seenLogins = new Set<string>();
        const mappedData: RecentLoginData[] = [];

        recentLoginsArray.forEach((item: any, index: number) => {
          const timestamp = item.timestamp || item._time || "";
          const message = item.message || "";
          const computerName = item.computerName || "Unknown";
          const dedupeKey = `${computerName}|${timestamp}|${message}`;

          if (seenLogins.has(dedupeKey)) return;
          seenLogins.add(dedupeKey);

          mappedData.push({
            id: `${item.computerId || computerName}_${timestamp}_${index}`,
            computerName,
            message,
            status: item.is_user_event === "true" ? "Valid" : "Invalid",
            timestamp,
            updatedAt: timestamp ? formatTimeAgo(timestamp) : "",
          });
        });

        // ✅ Sort by time (most recent first)
        const sortedData = mappedData.sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

        setRecentLogins(sortedData);
      } else {
        setRecentLogins([]);
      }
    } catch (error) {
      console.error("Error fetching recent login data:", error);
      setRecentLogins([]);
    }
    setLoadingLogins(false);
  };


  useEffect(() => {
    const loadData = async () => {
      if (!tenantName || !email) return;

      await Promise.all([
        fetchOverViewData().catch((error) => {
          console.error("Error fetching overview data:", error);
        }),
        fetchRecentLoginData().catch((error) => {
          console.error("Error fetching recent login data:", error);
        }),
      ]);
    };

    loadData();
  }, [selectedTime, tenantName, email, fromdetails, computerName, computerId]);




  useEffect(() => {
    if (fromdetails) removeCacheData("overviewKey");
  }, [fromdetails])

  const handleRefresh = () => {
    removeCacheData("overviewKey");
    void Promise.all([fetchOverViewData(true), fetchRecentLoginData()]);
  };

  return (
    <div className="p-6 space-y-6">
      {!fromdetails ? (
        <div className="flex justify-between items-center pb-4 border-b">
          <div className="flex-1">
            <TenantLists onSelectorChange={(value: string) => {
              setSelectedTenant(value)
              onSelectorChange?.(value)
            }} isLabelNeeded={true} />
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <label
                htmlFor="time-range-selector"
                className="absolute -top-4 left-3 z-10 px-1 text-xs font-medium text-muted-foreground"
              >
                Time Range
              </label>
              <Select value={selectedTime} onValueChange={setSelectedTime}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24Hrs">Last 24 Hours</SelectItem>
                  <SelectItem value="lw">Last 7 Days</SelectItem>
                  <SelectItem value="lm">Last 30 Days</SelectItem>
                  <SelectItem value="l3m">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" size="sm" className="flex items-center gap-2" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>
      ) : <div className="flex items-center justify-between gap-2">
        <div className="relative">
          <label
            htmlFor="time-range-selector"
            className="absolute -top-4 left-3 z-10 px-1 text-xs font-medium text-muted-foreground"
          >
            Time Range
          </label>
          <Select value={selectedTime} onValueChange={setSelectedTime}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24Hrs">Last 24 Hours</SelectItem>
              <SelectItem value="lw">Last 7 Days</SelectItem>
              <SelectItem value="lm">Last 30 Days</SelectItem>
              <SelectItem value="l3m">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" size="sm" className="flex items-center gap-2" onClick={handleRefresh}>
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>}

      {/* Overview Cards */}
      <div className="grid items-stretch grid-cols-1 gap-6 lg:grid-cols-2">
        <OverviewResourceTypes fromdetails={fromdetails} resourceType={mappedResourceType} loading={loadingOverview} />
        <OverviewEventTimeline fromdetails={fromdetails} timeline={mappedEventTimeline} loading={loadingOverview} />
      </div>

      {/* Activity and Recent Logins */}
      <div className="grid items-stretch grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex flex-col h-full gap-6">
          <TopResourceActivity
            fromdetails={fromdetails}
            topUsers={mappedTopUsers}
            topResources={mappedTopResources}
            topComputers={mappedTopComputers}
            loading={loadingOverview}
          />
          <RecentLoginDetails fromdetails={fromdetails} recentLogins={recentLogins} loading={loadingLogins} />
        </div>
        <OverviewRecentActivity fromdetails={fromdetails} recentdata={top10000Results} loading={loadingOverview} />
      </div>
    </div>
  );
}
